<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-[425px]">
            <DialogHeader>
                <DialogTitle>Add CPanel server</DialogTitle>
            </DialogHeader>
            <form @submit="onSubmit">
                <div class="space-y-3">
                    <FormField name="title" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Title (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="Title" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="host" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Host (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="Host" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="username" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Username (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="Username" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="password" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Password (Required)</FormLabel>
                            <FormControl>
                                <Password
                                    v-bind="componentField"
                                    placeholder="Password"
                                    :can-generate="false"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="token" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Token (Optional)</FormLabel>
                            <FormControl>
                                <Password
                                    v-bind="componentField"
                                    placeholder="Token"
                                    :can-generate="false"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
                <DialogFooter>
                    <Button type="button" variant="secondary" :disabled="saving" @click="close"
                        >Cancel</Button
                    >
                    <Button
                        type="submit"
                        :loading="saving"
                        :disabled="meta.valid === false || meta.dirty === false"
                        >Save</Button
                    >
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input, Password } from "@/components/ui/input";
import { cpanelGraphQLClient } from "@/composables/default-urql-client";
import { toTypedSchema } from "@vee-validate/zod";
import gql from "graphql-tag";
import { useForm } from "vee-validate";
import { ref } from "vue";
import { toast } from "vue-sonner";
import * as z from "zod";

const open = defineModel();

const emit = defineEmits(["success"]);
const saving = ref(false);

const schema = z.object({
    title: z.string().min(1, {
        message: "Title is required.",
    }),
    host: z.string().min(1, {
        message: "Host is required.",
    }),
    username: z.string().min(1, {
        message: "Username is required.",
    }),
    password: z.string().min(1, {
        message: "Password is required.",
    }),
    token: z.string().optional(),
});

type Form = z.infer<typeof schema>;

const { handleSubmit, meta, resetForm, setFieldValue } = useForm({
    validationSchema: toTypedSchema(schema),
});

const onSubmit = handleSubmit((values) => {
    save(values);
});

const close = () => {
    open.value = false;
};

const save = async (values: Form) => {
    saving.value = true;

    try {
        const { error } = await cpanelGraphQLClient.mutation(
            gql`
                mutation addCpanelServer(
                    $title: String!
                    $host: String!
                    $username: String!
                    $password: String!
                    $token: String
                ) {
                    createServer(
                        title: $title
                        host: $host
                        username: $username
                        password: $password
                        token: $token
                    ) {
                        id
                    }
                }
            `,
            {
                title: values.title,
                host: values.host,
                username: values.username,
                password: values.password,
                token: values.token,
            }
        );

        if (!error) {
            toast.success("success", {
                description: `Cpanel server was successfully created.`,
            });

            emit("success");
            close();
        }
    } finally {
        saving.value = false;
    }
};
</script>
